import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import Header from "../Header";
import Sidebar from "../Sidebar";
import "../../styles/theme.css";
import "../../styles/dashboard.css";
import "../../styles/ReportHistory.css";
import "../../styles/ReportDashboard.css";
import { useSidebar } from "../../App";
import { getApiUrl, getCsrfToken, getProdJobsUrl, localJobsFetch, readJobsApiTarget } from "../../utils/apiHelper";
import { useLocation, useNavigate } from "react-router-dom";
import SimpleScanProgressModal from "./SimpleScanProgressModal";
import RunTestModal from "./RunTestModal";
import ScheduleTestModal from "./ScheduleTestModal";
import { profileTypeLabels } from "../../utils/categoryLabels";
import ReportModal from "../reportComponents/ReportModal";

const TestStatus = () => {
  const { t, i18n } = useTranslation("attackSurfaceTestsStatusPage");
  const { sidebarCollapsed } = useSidebar();
  const location = useLocation();
  const navigate = useNavigate();
  const handledNavJobRef = useRef(false);
  const locale = (i18n.resolvedLanguage || i18n.language || "en").split("-")[0];
  const [loading, setLoading] = useState(true);
  const [jobs, setJobs] = useState([]);
  const [error, setError] = useState(null);
  const [showScanModal, setShowScanModal] = useState(false);
  const [selectedJobId, setSelectedJobId] = useState(null);
  const [showRunTestModal, setShowRunTestModal] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [nowMs, setNowMs] = useState(() => Date.now());
  const [timeSinceUpdate, setTimeSinceUpdate] = useState(() => t("time.shortSeconds", { count: 0 }));
  const [cancelingJobs, setCancelingJobs] = useState(new Set());
  const [showReportModal, setShowReportModal] = useState(false);
  const [selectedReport, setSelectedReport] = useState(null);
  const [loadingReport, setLoadingReport] = useState(false);
  const [loadingReportId, setLoadingReportId] = useState(null);
  const [activeTab, setActiveTab] = useState('ongoing');
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [selectedProfileIdForSchedule, setSelectedProfileIdForSchedule] = useState(null);
  const [schedules, setSchedules] = useState([]);
  const [loadingSchedules, setLoadingSchedules] = useState(false);
  const [scheduledDays, setScheduledDays] = useState(7);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [selectedErrorJob, setSelectedErrorJob] = useState(null);

  // Fetch job IDs from API
  const fetchTestStatus = async ({ silent = false } = {}) => {
    try {
      if (!silent) setLoading(true);
      setError(null);

      // Removed: user_id query parameter - user is obtained from session

      const response = await fetch(getProdJobsUrl('list/'), {
        method: "GET",
        credentials: 'include', // Send session cookies
        redirect: "follow"
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || errorData.detail || `HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      // console.log('Attack Surface Tests Status API Response:', result);

      // Get the current timestamp when API is called (this is the "last updated" time)
      const lastUpdatedTime = new Date().toISOString();

      // Handle the new API response format: {error: false, jobs: [...]}
      if (result.error === false && result.jobs && Array.isArray(result.jobs)) {
        // Filter out scheduled items and add last_updated timestamp to each job
        const jobsWithTimestamp = result.jobs
          .filter(job => {
            // Exclude scheduled items from jobs array
            const status = (job.status || job.job_status || '').toUpperCase();
            return status !== 'SCHEDULED' && 
                   job.schedule_id === undefined &&
                   !(job.frequency !== undefined && job.time_of_day !== undefined);
          })
          .map(job => ({
            ...job,
            last_updated: lastUpdatedTime
          }));
        setJobs(jobsWithTimestamp);
      } else if (result.error) {
        throw new Error(result.message || 'Error fetching jobs');
      } else {
        console.warn('Unexpected API response format:', result);
        setJobs([]);
      }
      
      // Reset last updated time when API call completes
      setLastUpdated(new Date());
      setTimeSinceUpdate(t("time.shortSeconds", { count: 0 }));
    } catch (error) {
      console.error("Error fetching Attack Surface Tests Status:", error);
      if (!silent) {
        setError(error.message);
        setJobs([]);
      }
    } finally {
      if (!silent) setLoading(false);
    }
  };

  useEffect(() => {
    fetchTestStatus();
  }, []);

  // Fetch scheduled tests
  const fetchSchedules = async () => {
    try {
      setLoadingSchedules(true);
      const response = await fetch(getProdJobsUrl('schedule/list/'), {
        method: "GET",
        credentials: 'include', // Send session cookies
        redirect: "follow"
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || errorData.detail || `HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      console.log('Scheduled Tests API Response:', result);

      if (result.error === false && result.schedules && Array.isArray(result.schedules)) {
        setSchedules(result.schedules);
      } else if (result.error) {
        throw new Error(result.message || 'Error fetching schedules');
      } else {
        console.warn('Unexpected API response format:', result);
        setSchedules([]);
      }
    } catch (error) {
      console.error("Error fetching scheduled tests:", error);
      setSchedules([]);
    } finally {
      setLoadingSchedules(false);
    }
  };

  // Calculate next run time for a schedule
  const calculateNextRunTime = (schedule) => {
    const now = new Date();

    if (schedule.frequency === 'single_run') {
      const runAt = new Date(schedule.run_at);
      return Number.isNaN(runAt.getTime()) ? null : runAt;
    }

    const [hours, minutes, seconds] = schedule.time_of_day.split(':').map(Number);
    const timeOfDay = new Date(now);
    timeOfDay.setHours(hours, minutes, seconds || 0, 0);

    if (schedule.frequency === 'daily') {
      // If today's time has passed, schedule for tomorrow
      if (timeOfDay <= now) {
        timeOfDay.setDate(timeOfDay.getDate() + 1);
      }
      return timeOfDay;
    } else if (schedule.frequency === 'weekly') {
      const dayMap = { 'mon': 1, 'tue': 2, 'wed': 3, 'thu': 4, 'fri': 5, 'sat': 6, 'sun': 0 };
      const targetDay = dayMap[schedule.day_of_week];
      const currentDay = now.getDay();
      
      let daysUntilTarget = targetDay - currentDay;
      if (daysUntilTarget < 0 || (daysUntilTarget === 0 && timeOfDay <= now)) {
        daysUntilTarget += 7; // Next week
      }
      
      timeOfDay.setDate(timeOfDay.getDate() + daysUntilTarget);
      return timeOfDay;
    } else if (schedule.frequency === 'monthly') {
      const targetDay = schedule.day_of_month;
      
      // Helper function to get the correct date for a given month
      const getDateForMonth = (year, month, day) => {
        const date = new Date(year, month, day, hours, minutes, seconds || 0);
        // If the day rolled over, it means the day doesn't exist in this month (e.g., Feb 30)
        // Use the last day of the month instead
        if (date.getDate() !== day) {
          // Go to first day of next month, then back one day to get last day of target month
          date.setMonth(month + 1, 0);
          date.setHours(hours, minutes, seconds || 0, 0);
        }
        return date;
      };
      
      // Try this month first
      let nextRun = getDateForMonth(now.getFullYear(), now.getMonth(), targetDay);
      
      // If this month's date has passed, move to next month
      if (nextRun <= now) {
        const nextMonth = now.getMonth() + 1;
        const nextYear = nextMonth === 12 ? now.getFullYear() + 1 : now.getFullYear();
        nextRun = getDateForMonth(nextYear, nextMonth % 12, targetDay);
      }
      
      return nextRun;
    }
    
    return null;
  };

  // Generate all occurrences for a schedule within the selected number of days
  const generateOccurrencesForSelectedDays = (schedule) => {
    const now = new Date();
    const endDate = new Date(now);
    endDate.setDate(endDate.getDate() + scheduledDays);
    endDate.setHours(23, 59, 59, 999); // End of selected day
    
    const occurrences = [];

    if (schedule.frequency === 'single_run') {
      const runAt = new Date(schedule.run_at);
      if (!Number.isNaN(runAt.getTime()) && runAt > now && runAt <= endDate) {
        occurrences.push(runAt);
      }
      return occurrences;
    }

    const [hours, minutes, seconds] = schedule.time_of_day.split(':').map(Number);

    if (schedule.frequency === 'daily') {
      // Generate all daily occurrences for the next 7 days
      let currentDate = new Date(now);
      currentDate.setHours(hours, minutes, seconds || 0, 0);
      
      // If today's time has passed, start from tomorrow
      if (currentDate <= now) {
        currentDate.setDate(currentDate.getDate() + 1);
      }
      
      // Generate occurrences for the selected number of days
      while (currentDate <= endDate) {
        occurrences.push(new Date(currentDate));
        currentDate.setDate(currentDate.getDate() + 1);
      }
    } else if (schedule.frequency === 'weekly') {
      // Generate all weekly occurrences within the selected number of days
      const dayMap = { 'mon': 1, 'tue': 2, 'wed': 3, 'thu': 4, 'fri': 5, 'sat': 6, 'sun': 0 };
      const targetDay = dayMap[schedule.day_of_week];
      const currentDay = now.getDay();
      
      let daysUntilTarget = targetDay - currentDay;
      if (daysUntilTarget < 0 || (daysUntilTarget === 0 && new Date(now.getFullYear(), now.getMonth(), now.getDate(), hours, minutes, seconds || 0) <= now)) {
        daysUntilTarget += 7; // Next week
      }
      
      let nextOccurrence = new Date(now);
      nextOccurrence.setDate(nextOccurrence.getDate() + daysUntilTarget);
      nextOccurrence.setHours(hours, minutes, seconds || 0, 0);
      
      // Generate all weekly occurrences within the selected number of days
      while (nextOccurrence <= endDate) {
        occurrences.push(new Date(nextOccurrence));
        nextOccurrence.setDate(nextOccurrence.getDate() + 7); // Next week
      }
    } else if (schedule.frequency === 'monthly') {
      // Generate all monthly occurrences within the selected number of days
      const targetDay = schedule.day_of_month;
      
      const getDateForMonth = (year, month, day) => {
        const date = new Date(year, month, day, hours, minutes, seconds || 0);
        if (date.getDate() !== day) {
          date.setMonth(month + 1, 0);
          date.setHours(hours, minutes, seconds || 0, 0);
        }
        return date;
      };
      
      let nextRun = getDateForMonth(now.getFullYear(), now.getMonth(), targetDay);
      
      if (nextRun <= now) {
        const nextMonth = now.getMonth() + 1;
        const nextYear = nextMonth === 12 ? now.getFullYear() + 1 : now.getFullYear();
        nextRun = getDateForMonth(nextYear, nextMonth % 12, targetDay);
      }
      
      // Generate all monthly occurrences within the selected number of days
      while (nextRun <= endDate) {
        occurrences.push(new Date(nextRun));
        const nextMonth = nextRun.getMonth() + 1;
        const nextYear = nextMonth === 12 ? nextRun.getFullYear() + 1 : nextRun.getFullYear();
        nextRun = getDateForMonth(nextYear, nextMonth % 12, targetDay);
      }
    }

    return occurrences;
  };

  // Get all scheduled test occurrences for the selected number of days
  const getNextScheduledTests = () => {
    const allOccurrences = [];
    
    schedules.forEach(schedule => {
      const occurrences = generateOccurrencesForSelectedDays(schedule);
      occurrences.forEach(nextRun => {
        allOccurrences.push({
          ...schedule,
          next_run: nextRun
        });
      });
    });

    // Sort by next run time
    return allOccurrences.sort((a, b) => a.next_run - b.next_run);
  };

  // Fetch schedules when scheduled tab is active
  useEffect(() => {
    if (activeTab === 'scheduled') {
      fetchSchedules();
    }
  }, [activeTab]);

  // If we arrived here from Dashboard with a jobId, open the status modal immediately.
  // `SimpleScanProgressModal` polls by jobId, so we don't need to wait for the jobs list.
  useEffect(() => {
    const navJobId = location.state?.jobId;
    if (!navJobId || handledNavJobRef.current) return;

    handledNavJobRef.current = true;
    setSelectedJobId(navJobId);
    setShowScanModal(true);
    fetchTestStatus();

    // Clear router state so a refresh/back/forward doesn't reopen the modal repeatedly.
    navigate("/status", { replace: true, state: {} });
  }, [location.state, navigate]);

  // Update time since last update every second
  useEffect(() => {
    if (!lastUpdated) return;

    const updateTimer = () => {
      const now = new Date();
      setNowMs(now.getTime());
      const diffMs = now - lastUpdated;
      const diffSeconds = Math.floor(diffMs / 1000);
      const diffMinutes = Math.floor(diffMs / 60000);
      const diffHours = Math.floor(diffMs / 3600000);

      if (diffSeconds < 60) {
        setTimeSinceUpdate(t("time.shortSeconds", { count: diffSeconds }));
      } else if (diffMinutes < 60) {
        setTimeSinceUpdate(t("time.shortMinutes", { count: diffMinutes }));
      } else {
        setTimeSinceUpdate(t("time.shortHours", { count: diffHours }));
      }
    };

    // Update immediately
    updateTimer();

    // Update every second
    const interval = setInterval(updateTimer, 1000);

    return () => clearInterval(interval);
  }, [lastUpdated, t]);

  const statusLabel = (status) =>
    status ? t(`status.${status}`, { defaultValue: status }) : t("notAvailable");

  const formatDuration = (startValue, endValue) => {
    if (!startValue || endValue == null) return t("notAvailable");
    try {
      const start = new Date(startValue);
      const end = endValue instanceof Date ? endValue : new Date(endValue);
      const diffMs = Math.max(0, end - start);
      if (Number.isNaN(diffMs)) return t("notAvailable");
      const hours = Math.floor(diffMs / 3600000);
      const minutes = Math.floor((diffMs % 3600000) / 60000);
      const seconds = Math.floor((diffMs % 60000) / 1000);
      if (hours > 0) return `${hours}h ${minutes}m ${seconds}s`;
      if (minutes > 0) return `${minutes}m ${seconds}s`;
      return `${seconds}s`;
    } catch (e) {
      return t("notAvailable");
    }
  };

  // Format timestamp for display
  const formatTimestamp = (timestamp) => {
    if (!timestamp) return t("notAvailable");
    try {
      const date = new Date(timestamp);
      return date.toLocaleString(locale);
    } catch (error) {
      console.error("Error formatting timestamp:", error);
      return t("invalidDate");
    }
  };

  // Format relative time (seconds/minutes/hours ago) or date if previous day
  const formatRelativeTime = (timestamp) => {
    if (!timestamp) return t("notAvailable");
    try {
      const date = new Date(timestamp);
      const now = new Date();
      const diffMs = now - date;
      const diffSeconds = Math.floor(diffMs / 1000);
      const diffMinutes = Math.floor(diffMs / 60000);
      const diffHours = Math.floor(diffMs / 3600000);
      
      // Check if it's the same day
      const isToday = date.toDateString() === now.toDateString();
      
      if (isToday) {
        if (diffSeconds < 60) {
          return diffSeconds === 1
            ? t("time.secondAgo", { count: diffSeconds })
            : t("time.secondsAgo", { count: diffSeconds });
        } else if (diffMinutes < 60) {
          return diffMinutes === 1
            ? t("time.minuteAgo", { count: diffMinutes })
            : t("time.minutesAgo", { count: diffMinutes });
        } else {
          return diffHours === 1
            ? t("time.hourAgo", { count: diffHours })
            : t("time.hoursAgo", { count: diffHours });
        }
      } else {
        // Show date for previous days
        return date.toLocaleDateString(locale);
      }
    } catch (error) {
      console.error("Error formatting relative time:", error);
      return t("invalidDate");
    }
  };

  // Show loading state
  if (loading) {
    return (
      <div>
        <Header />
        <div className="main-container">
          <div className={`${sidebarCollapsed ? 'col-1' : 'col-2'} p-0`}>
            <Sidebar />
          </div>
          <main className={`${sidebarCollapsed ? 'col-11' : 'col-10'}`} style={{ width: "100%", overflowX: "auto" }}>
            <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "50vh" }}>
              <div style={{ textAlign: "center" }}>
                <div style={{ fontSize: "18px", marginBottom: "10px" }}>{t("loading")}</div>
                <div style={{ fontSize: "14px", color: "#666" }}>{t("loadingSubtitle")}</div>
              </div>
            </div>
          </main>
        </div>
      </div>
    );
  }

  // Show error state
  if (error) {
    return (
      <div>
        <Header />
        <div className="main-container">
          <div className={`${sidebarCollapsed ? 'col-1' : 'col-2'} p-0`}>
            <Sidebar />
          </div>
          <main className={`${sidebarCollapsed ? 'col-11' : 'col-10'}`} style={{ width: "100%", overflowX: "auto" }}>
            <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "50vh" }}>
              <div style={{ textAlign: "center" }}>
                <div style={{ fontSize: "18px", marginBottom: "10px", color: "#d32f2f" }}>{t("errorTitle")}</div>
                <div style={{ fontSize: "14px", color: "#666", marginBottom: "20px" }}>{error}</div>
                <button
                  onClick={fetchTestStatus}
                  className="btn btn-primary"
                  style={{ marginRight: "10px" }}
                >
                  {t("retry")}
                </button>
              </div>
            </div>
          </main>
        </div>
      </div>
    );
  }

  // Helper function to check if job is clickable (RUNNING or PENDING)
  const isJobClickable = (job) => {
    const status = (job.status || job.job_status || '').toUpperCase();
    return status === 'RUNNING' || status === 'PENDING';
  };

  // Helper function to check if job can be canceled (only RUNNING or PENDING)
  const canCancelJob = (job) => {
    const status = (job.status || job.job_status || '').toUpperCase();
    return status === 'RUNNING' || status === 'PENDING';
  };

  // Helper function to check if job has a report (COMPLETED with report_id)
  const hasReport = (job) => {
    const status = (job.status || job.job_status || '').toUpperCase();
    return !!(status === 'COMPLETED' && job.hasOwnProperty('report_id') && job.report_id != null);
  };

  // Helper function to check if a job is a scheduled item (should be excluded from job tabs)
  const isScheduledItem = (job) => {
    const status = (job.status || job.job_status || '').toUpperCase();
    // Check for SCHEDULED status or schedule-related properties
    return status === 'SCHEDULED' || 
           job.schedule_id !== undefined ||
           (job.frequency !== undefined && job.time_of_day !== undefined);
  };

  // Helper functions to filter jobs by category
  const getOngoingJobs = () => {
    return jobs.filter(job => {
      // Explicitly exclude scheduled items
      if (isScheduledItem(job)) {
        return false;
      }
      const status = (job.status || job.job_status || '').toUpperCase();
      return status === 'RUNNING' || status === 'PENDING';
    });
  };

  const getCompletedJobs = () => {
    return jobs.filter(job => {
      // Explicitly exclude scheduled items
      if (isScheduledItem(job)) {
        return false;
      }
      const status = (job.status || job.job_status || '').toUpperCase();
      return status === 'COMPLETED';
    });
  };

  const getErrorJobs = () => {
    return jobs.filter(job => {
      // Explicitly exclude scheduled items
      if (isScheduledItem(job)) {
        return false;
      }
      const status = (job.status || job.job_status || '').toUpperCase();
      return status === 'FAILED' || status === 'CANCELED' || status === 'CANCELLED' || 
             (status !== 'RUNNING' && status !== 'PENDING' && status !== 'COMPLETED');
    });
  };

  // Get filtered jobs based on active tab (excludes scheduled - handled separately)
  const getFilteredJobs = () => {
    switch (activeTab) {
      case 'ongoing':
        return getOngoingJobs();
      case 'completed':
        return getCompletedJobs();
      case 'errors':
        return getErrorJobs();
      default:
        return [];
    }
  };

  // Handle viewing report
  const handleViewReport = async (reportId, e) => {
    if (e?.stopPropagation) {
      e.stopPropagation(); // Prevent row click from firing
    }

    try {
      setLoadingReport(true);
      setLoadingReportId(reportId);
      
      const response = await fetch(getApiUrl(`/api/internal/readiness-score/getReportById/${reportId}/`), {
        method: "GET",
        credentials: 'include', // Send session cookies
        redirect: "follow"
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const detailedReport = await response.json();
      setSelectedReport(detailedReport);
      setShowReportModal(true);
    } catch (error) {
      console.error("Error fetching report:", error);
      alert(t("alerts.loadReportFailed", { reportId, message: error.message }));
    } finally {
      setLoadingReport(false);
      setLoadingReportId(null);
    }
  };

  // Handle cancel job
  const handleCancelJob = async (jobId, e) => {
    e.stopPropagation(); // Prevent row click from firing
    
    if (!window.confirm(t("alerts.cancelConfirm", { jobId }))) {
      return;
    }

    try {
      // Add job to canceling set
      setCancelingJobs(prev => new Set(prev).add(jobId));

      // Get CSRF token for session-based authentication
      const csrfToken = await getCsrfToken();

      // Create empty FormData as required by the API
      const formData = new FormData();

      let response;
      if (readJobsApiTarget() === "localhost") {
        response = await localJobsFetch(`${jobId}/delete/`, {
          method: "DELETE",
          body: formData,
        });
      } else {
        response = await fetch(getProdJobsUrl(`${jobId}/delete/`), {
          method: "DELETE",
          credentials: "include",
          headers: {
            "X-CSRFToken": csrfToken,
          },
          body: formData,
          redirect: "follow",
        });
      }

      const result = await response.json().catch(async () => {
        // If JSON parsing fails, try text
        const text = await response.text().catch(() => '');
        throw new Error(text || `Failed to cancel job: ${response.status}`);
      });

      if (!response.ok) {
        throw new Error(result.message || result.detail || `Failed to cancel job: ${response.status}`);
      }

      // Check if the response indicates success
      if (result.error === false) {
        // Show success message
        alert(t("alerts.cancelSuccess", { message: result.message || t("alerts.cancelSuccessDefault") }));
        console.log('Cancel job response:', result);
        
        // Refresh the jobs list after successful cancellation
        await fetchTestStatus();
      } else {
        throw new Error(result.message || 'Failed to cancel job');
      }
    } catch (error) {
      console.error("Error canceling job:", error);
      alert(t("alerts.cancelFailed", { message: error.message }));
    } finally {
      // Remove job from canceling set
      setCancelingJobs(prev => {
        const newSet = new Set(prev);
        newSet.delete(jobId);
        return newSet;
      });
    }
  };

  return (
    <div>
      <Header />
      <div className="main-container">
        <div className={`${sidebarCollapsed ? 'col-1' : 'col-2'} p-0`}>
          <Sidebar />
        </div>
        <main className={`${sidebarCollapsed ? 'col-11' : 'col-10'}`} style={{ width: "100%", overflowX: "auto" }}>
          <div className="dashboard">
            <div className="profile" style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <h1>{t("title")}</h1>
              <div style={{ display: "flex", gap: "10px" }}>
                <button
                  onClick={() => setShowRunTestModal(true)}
                  className="btn btn-primary"
                >
                  {t("runTests")}
                </button>
                <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                  <button
                    onClick={() => {
                      fetchTestStatus();
                      if (activeTab === 'scheduled') {
                        fetchSchedules();
                      }
                    }}
                    className="btn btn-secondary"
                    disabled={loading || loadingSchedules}
                  >
                    {loading || loadingSchedules ? t("refreshing") : t("refresh")}
                  </button>
                  {lastUpdated && (
                    <span style={{ color: "var(--card-text)", fontSize: "14px" }}>
                      {t("lastUpdated", { time: timeSinceUpdate })}
                    </span>
                  )}
                </div>
              </div>
            </div>

            {/* Tabs */}
            <div className="panel-default2 padding2" style={{ marginBottom: "20px" }}>
              <div style={{ 
                display: "flex", 
                borderBottom: "2px solid var(--card-border)",
                marginBottom: "20px"
              }}>
                <button
                  onClick={() => setActiveTab('ongoing')}
                  style={{
                    padding: "12px 24px",
                    fontSize: "14px",
                    fontWeight: activeTab === 'ongoing' ? "600" : "400",
                    color: activeTab === 'ongoing' ? "var(--primary-color, #4a90e2)" : "var(--card-text)",
                    backgroundColor: "transparent",
                    border: "none",
                    borderBottom: activeTab === 'ongoing' ? "2px solid var(--primary-color, #4a90e2)" : "2px solid transparent",
                    cursor: "pointer",
                    transition: "all 0.2s ease",
                    marginBottom: "-2px"
                  }}
                >
                  {t("tabs.ongoing", { count: getOngoingJobs().length })}
                </button>
                <button
                  onClick={() => setActiveTab('completed')}
                  style={{
                    padding: "12px 24px",
                    fontSize: "14px",
                    fontWeight: activeTab === 'completed' ? "600" : "400",
                    color: activeTab === 'completed' ? "var(--primary-color, #4a90e2)" : "var(--card-text)",
                    backgroundColor: "transparent",
                    border: "none",
                    borderBottom: activeTab === 'completed' ? "2px solid var(--primary-color, #4a90e2)" : "2px solid transparent",
                    cursor: "pointer",
                    transition: "all 0.2s ease",
                    marginBottom: "-2px"
                  }}
                >
                  {t("tabs.completed", { count: getCompletedJobs().length })}
                </button>
                <button
                  onClick={() => setActiveTab('errors')}
                  style={{
                    padding: "12px 24px",
                    fontSize: "14px",
                    fontWeight: activeTab === 'errors' ? "600" : "400",
                    color: activeTab === 'errors' ? "var(--primary-color, #4a90e2)" : "var(--card-text)",
                    backgroundColor: "transparent",
                    border: "none",
                    borderBottom: activeTab === 'errors' ? "2px solid var(--primary-color, #4a90e2)" : "2px solid transparent",
                    cursor: "pointer",
                    transition: "all 0.2s ease",
                    marginBottom: "-2px"
                  }}
                >
                  {t("tabs.errors", { count: getErrorJobs().length })}
                </button>
                <button
                  onClick={() => setActiveTab('scheduled')}
                  style={{
                    padding: "12px 24px",
                    fontSize: "14px",
                    fontWeight: activeTab === 'scheduled' ? "600" : "400",
                    color: activeTab === 'scheduled' ? "var(--primary-color, #4a90e2)" : "var(--card-text)",
                    backgroundColor: "transparent",
                    border: "none",
                    borderBottom: activeTab === 'scheduled' ? "2px solid var(--primary-color, #4a90e2)" : "2px solid transparent",
                    cursor: "pointer",
                    transition: "all 0.2s ease",
                    marginBottom: "-2px"
                  }}
                >
                  {t("tabs.scheduled")}
                </button>
              </div>

              {/* Table Content */}
              {activeTab === 'scheduled' ? (
                <div key="scheduled-tab-content">
                  {/* Days selector dropdown */}
                  <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", marginBottom: "20px", gap: "10px" }}>
                    <label style={{ 
                      color: "var(--card-text)", 
                      fontSize: "14px",
                      fontWeight: "500"
                    }}>
                      {t("scheduled.showNext")}
                    </label>
                    <select
                      value={scheduledDays}
                      onChange={(e) => setScheduledDays(parseInt(e.target.value, 10))}
                      style={{
                        padding: "8px 36px 8px 12px",
                        borderRadius: "6px",
                        border: "1px solid var(--card-border)",
                        backgroundColor: "var(--card-bg)",
                        color: "var(--card-text)",
                        fontSize: "14px",
                        outline: "none",
                        cursor: "pointer",
                        minWidth: "80px",
                        appearance: "none",
                        WebkitAppearance: "none",
                        MozAppearance: "none",
                        backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%236b7280' d='M6 9L1 4h10z'/%3E%3C/svg%3E\")",
                        backgroundRepeat: "no-repeat",
                        backgroundPosition: "right 12px center",
                        backgroundSize: "12px",
                      }}
                    >
                      <option value={7}>{t("scheduled.days", { count: 7 })}</option>
                      <option value={14}>{t("scheduled.days", { count: 14 })}</option>
                      <option value={30}>{t("scheduled.days", { count: 30 })}</option>
                      <option value={60}>{t("scheduled.days", { count: 60 })}</option>
                      <option value={90}>{t("scheduled.days", { count: 90 })}</option>
                    </select>
                  </div>
                  {loadingSchedules ? (
                    <div style={{ textAlign: "center", padding: "40px", color: "var(--card-text)" }}>
                      <p>{t("scheduled.loading")}</p>
                    </div>
                  ) : getNextScheduledTests().length > 0 ? (
                    <div style={{ overflowX: "auto" }}>
                      <table style={{ width: "100%", borderCollapse: "collapse" }}>
                        <thead>
                          <tr style={{ borderBottom: "2px solid var(--card-border)" }}>
                            <th style={{ padding: "12px", textAlign: "left", color: "var(--card-text)" }}>{t("table.status")}</th>
                            <th style={{ padding: "12px", textAlign: "left", color: "var(--card-text)" }}>{t("table.nextRun")}</th>
                            <th style={{ padding: "12px", textAlign: "left", color: "var(--card-text)" }}>{t("table.profileName")}</th>
                            <th style={{ padding: "12px", textAlign: "left", color: "var(--card-text)" }}>{t("table.profileType")}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {getNextScheduledTests().map((schedule, index) => {
                            return (
                              <tr 
                                key={`scheduled-${schedule.schedule_id}-${index}`}
                                style={{ 
                                  borderBottom: "1px solid var(--card-border)"
                                }}
                              >
                                <td style={{ padding: "12px" }}>
                                  <span
                                    style={{
                                      padding: "4px 12px",
                                      borderRadius: "12px",
                                      fontSize: "12px",
                                      fontWeight: "bold",
                                      backgroundColor: "#7c3aed20",
                                      color: "#7c3aed",
                                    }}
                                  >
                                    {statusLabel("SCHEDULED")}
                                  </span>
                                </td>
                                <td style={{ padding: "12px", color: "var(--card-text)" }}>
                                  {schedule.next_run ? formatTimestamp(schedule.next_run.toISOString()) : t("notAvailable")}
                                </td>
                                <td style={{ padding: "12px", color: "var(--card-text)" }}>
                                  {schedule.profile_name || t("notAvailable")}
                                </td>
                                <td style={{ padding: "12px", color: "var(--card-text)" }}>
                                  {schedule.profile_type ? (profileTypeLabels[schedule.profile_type] || schedule.profile_type) : t("notAvailable")}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div style={{ textAlign: "center", padding: "40px", color: "var(--card-text)" }}>
                      <p>{t("scheduled.empty")}</p>
                      <p style={{ fontSize: "14px", color: "#666", marginTop: "10px" }}>
                        {t("scheduled.emptyHint")}
                      </p>
                    </div>
                  )}
                </div>
              ) : (
                <div key="jobs-tab-content">
                  {getFilteredJobs().length > 0 ? (
                <div style={{ overflowX: "hidden" }}>
                  <table style={{ width: "100%", borderCollapse: "collapse", tableLayout: "fixed" }}>
                    <thead>
                      <tr style={{ borderBottom: "2px solid var(--card-border)" }}>
                        <th style={{ padding: "12px", textAlign: "left", color: "var(--card-text)", width: "7%" }}>{t("table.id")}</th>
                        <th style={{ padding: "12px", textAlign: "left", color: "var(--card-text)", width: "12%" }}>{t("table.status")}</th>
                        <th style={{ padding: "12px", textAlign: "left", color: "var(--card-text)", width: "20%" }}>{t("table.profileName")}</th>
                        <th style={{ padding: "12px", textAlign: "left", color: "var(--card-text)", width: "15%" }}>{t("table.profileType")}</th>
                        <th style={{ padding: "12px", textAlign: "left", color: "var(--card-text)", width: "14%" }}>{t("table.started")}</th>
                        <th style={{ padding: "12px", textAlign: "left", color: "var(--card-text)", width: "14%" }}>{t("table.finished")}</th>
                        <th style={{ padding: "12px", textAlign: "left", color: "var(--card-text)", width: "8%" }}>{t("table.duration")}</th>
                        <th style={{ padding: "12px", textAlign: "left", color: "var(--card-text)", width: "10%" }}>{t("table.actions")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {getFilteredJobs().map((job, index) => {
                        const jobId = job.job_id || job.id || job.jobId || `Job-${index + 1}`;
                        const status = (job.status || job.job_status || 'PENDING').toUpperCase();
                        const started = job.started_at || job.created_at;
                        const finished = job.finished_at;
                        const clickable = isJobClickable(job);
                        const isLive = status === "RUNNING" || status === "PENDING";
                        // Finished jobs use start → finish. Ongoing jobs tick from start until now.
                        const durationEnd = finished || (isLive && started ? nowMs : null);
                        const duration = formatDuration(started, durationEnd);

                        // Determine status badge colors (handles both 'canceled' and 'cancelled' spellings)
                        let statusBgColor, statusColor;
                        if (status === 'RUNNING') {
                          statusBgColor = "#daa52020";
                          statusColor = "#daa520";
                        } else if (status === 'PENDING') {
                          statusBgColor = "#4a90e220";
                          statusColor = "#4a90e2";
                        } else if (status === 'COMPLETED') {
                          statusBgColor = "#00800020";
                          statusColor = "#008000";
                        } else if (status === 'FAILED') {
                          statusBgColor = "#ab2c1820";
                          statusColor = "#ab2c18";
                        } else if (status === 'CANCELED' || status === 'CANCELLED') {
                          statusBgColor = "#6b728020";
                          statusColor = "#6b7280";
                        } else {
                          statusBgColor = "#6b728020";
                          statusColor = "#6b7280";
                        }

                        return (
                          <tr 
                            key={index} 
                            onClick={() => {
                              if (clickable) {
                                // Open scan progress modal for running/pending jobs
                                setSelectedJobId(jobId);
                                setShowScanModal(true);
                              }
                            }}
                            style={{ 
                              borderBottom: "1px solid var(--card-border)",
                              cursor: clickable ? "pointer" : "default",
                              transition: "background-color 0.2s ease",
                              opacity: clickable ? 1 : 0.7
                            }}
                            onMouseEnter={(e) => {
                              if (clickable) {
                                e.currentTarget.style.backgroundColor = "var(--card-border)";
                              }
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.backgroundColor = "transparent";
                            }}
                          >
                            <td style={{ padding: "12px", color: "var(--card-text)", fontFamily: "monospace" }}>
                              {jobId}
                            </td>
                            <td style={{ padding: "12px" }}>
                              <span
                                style={{
                                  padding: "4px 12px",
                                  borderRadius: "12px",
                                  fontSize: "12px",
                                  fontWeight: "bold",
                                  backgroundColor: statusBgColor,
                                  color: statusColor,
                                }}
                              >
                                {statusLabel(status)}
                              </span>
                            </td>
                            <td style={{ padding: "12px", color: "var(--card-text)", overflow: "hidden" }}>
                              <span
                                title={job.profile_name || t("notAvailable")}
                                style={{
                                  display: "block",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                }}
                              >
                                {job.profile_name || t("notAvailable")}
                              </span>
                            </td>
                            <td style={{ padding: "12px", color: "var(--card-text)", overflowWrap: "anywhere" }}>
                              {job.profile_type ? (profileTypeLabels[job.profile_type] || job.profile_type) : t("notAvailable")}
                            </td>
                            <td style={{ padding: "12px", color: "var(--card-text)" }}>
                              {started ? formatTimestamp(started) : t("notAvailable")}
                            </td>
                            <td style={{ padding: "12px", color: "var(--card-text)" }}>
                              {finished ? formatTimestamp(finished) : "-"}
                            </td>
                            <td style={{ padding: "12px", color: "var(--card-text)" }}>
                              {duration}
                            </td>
                            <td style={{ padding: "12px" }}>
                              <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
                                {canCancelJob(job) && (
                                  <button
                                    onClick={(e) => handleCancelJob(jobId, e)}
                                    disabled={cancelingJobs.has(jobId)}
                                    style={{
                                      padding: "6px 12px",
                                      fontSize: "12px",
                                      backgroundColor: cancelingJobs.has(jobId) ? "#6b7280" : "#dc2626",
                                      color: "white",
                                      border: "none",
                                      borderRadius: "6px",
                                      cursor: cancelingJobs.has(jobId) ? "not-allowed" : "pointer",
                                      fontWeight: "500",
                                      transition: "background-color 0.2s ease"
                                    }}
                                    onMouseEnter={(e) => {
                                      if (!cancelingJobs.has(jobId)) {
                                        e.currentTarget.style.backgroundColor = "#b91c1c";
                                      }
                                    }}
                                    onMouseLeave={(e) => {
                                      if (!cancelingJobs.has(jobId)) {
                                        e.currentTarget.style.backgroundColor = "#dc2626";
                                      }
                                    }}
                                  >
                                    {cancelingJobs.has(jobId) ? t("actions.canceling") : t("actions.cancel")}
                                  </button>
                                )}
                                {hasReport(job) && (
                                  <button
                                    onClick={(e) => handleViewReport(job.report_id, e)}
                                    disabled={loadingReportId === job.report_id}
                                    style={{
                                      padding: "6px 12px",
                                      fontSize: "12px",
                                      backgroundColor: loadingReportId === job.report_id ? "#6b7280" : "#008000",
                                      color: "white",
                                      border: "none",
                                      borderRadius: "6px",
                                      cursor: loadingReportId === job.report_id ? "not-allowed" : "pointer",
                                      fontWeight: "500",
                                      transition: "background-color 0.2s ease"
                                    }}
                                    onMouseEnter={(e) => {
                                      if (loadingReportId !== job.report_id) {
                                        e.currentTarget.style.backgroundColor = "#006600";
                                      }
                                    }}
                                    onMouseLeave={(e) => {
                                      if (loadingReportId !== job.report_id) {
                                        e.currentTarget.style.backgroundColor = "#008000";
                                      }
                                    }}
                                  >
                                    {loadingReportId === job.report_id ? t("actions.loading") : t("actions.report")}
                                  </button>
                                )}
                                {status === 'FAILED' && (
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      const fallbackErrorMessage =
                                        job.error_message ||
                                        job.error ||
                                        job.message ||
                                        job.detail ||
                                        job.error_msg ||
                                        "";
                                      setSelectedErrorJob({
                                        jobId,
                                        status,
                                        profile_name: job.profile_name,
                                        profile_type: job.profile_type,
                                        error_message: fallbackErrorMessage,
                                      });
                                      setShowErrorModal(true);
                                    }}
                                    style={{
                                      padding: "6px 12px",
                                      fontSize: "12px",
                                      backgroundColor: "#dc2626",
                                      color: "white",
                                      border: "none",
                                      borderRadius: "6px",
                                      cursor: "pointer",
                                      fontWeight: "500",
                                      transition: "background-color 0.2s ease"
                                    }}
                                    onMouseEnter={(e) => {
                                      e.currentTarget.style.backgroundColor = "#b91c1c";
                                    }}
                                    onMouseLeave={(e) => {
                                      e.currentTarget.style.backgroundColor = "#dc2626";
                                    }}
                                  >
                                    {t("actions.info")}
                                  </button>
                                )}
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                  ) : (
                    <div style={{ textAlign: "center", padding: "40px", color: "var(--card-text)" }}>
                      <p>
                        {activeTab === 'ongoing'
                          ? t("empty.ongoing")
                          : activeTab === 'completed'
                          ? t("empty.completed")
                          : t("empty.errors")}
                      </p>
                      <p style={{ fontSize: "14px", color: "#666", marginTop: "10px" }}>
                        {activeTab === 'ongoing'
                          ? t("empty.ongoingHint")
                          : activeTab === 'completed'
                          ? t("empty.completedHint")
                          : t("empty.errorsHint")}
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>

         
          </div>
        </main>
      </div>
      <SimpleScanProgressModal
        isOpen={showScanModal}
        onClose={() => {
          setShowScanModal(false);
          setSelectedJobId(null);
          fetchTestStatus({ silent: true });
        }}
        jobId={selectedJobId}
        onViewReport={(reportId) => {
          setShowScanModal(false);
          setSelectedJobId(null);
          fetchTestStatus({ silent: true });
          handleViewReport(reportId);
        }}
      />
      <RunTestModal 
        isOpen={showRunTestModal} 
        onClose={() => setShowRunTestModal(false)}
        onTestStarted={(jobId) => {
          // Open the scan progress modal for the new job
          setSelectedJobId(jobId);
          setShowScanModal(true);
          // Refresh the jobs list after test is started
          fetchTestStatus();
        }}
        onScheduleTest={(profileId) => {
          // Open the schedule modal with the selected profile
          setSelectedProfileIdForSchedule(profileId);
          setShowScheduleModal(true);
        }}
      />
      <ScheduleTestModal
        isOpen={showScheduleModal}
        onClose={() => {
          setShowScheduleModal(false);
          setSelectedProfileIdForSchedule(null);
        }}
        selectedProfileId={selectedProfileIdForSchedule}
        onScheduleCreated={(result) => {
          // Refresh the jobs list and schedules after test is scheduled
          fetchTestStatus();
          if (activeTab === 'scheduled') {
            fetchSchedules();
          }
        }}
      />
      {/* Modal for FAILED job error details */}
      {showErrorModal && selectedErrorJob && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 10000,
          }}
          onClick={() => {
            setShowErrorModal(false);
            setSelectedErrorJob(null);
          }}
        >
          <div
            style={{
              backgroundColor: "var(--card-bg, #ffffff)",
              color: "var(--card-text, #111827)",
              borderRadius: "12px",
              padding: "20px 22px 18px 22px",
              maxWidth: "640px",
              width: "92%",
              boxShadow: "0 18px 40px rgba(15, 23, 42, 0.35)",
              border: "1px solid var(--card-border, #e5e7eb)",
              position: "relative",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "10px",
              }}
            >
              <h2
                style={{
                  margin: 0,
                  fontSize: "17px",
                  fontWeight: 600,
                }}
              >
                {t("errorModal.title")}
              </h2>
              <button
                onClick={() => {
                  setShowErrorModal(false);
                  setSelectedErrorJob(null);
                }}
                style={{
                  border: "none",
                  background: "transparent",
                  color: "#6b7280",
                  fontSize: "18px",
                  cursor: "pointer",
                  padding: "2px 4px",
                }}
              >
                ×
              </button>
            </div>
            {/* Error message first (primary content) */}
            <div style={{ marginTop: "2px", marginBottom: "10px" }}>
              <div
                style={{
                  marginBottom: "6px",
                  fontSize: "13px",
                  fontWeight: 500,
                }}
              >
                {t("errorModal.errorMessage")}
              </div>
              <div
                style={{
                  maxHeight: "260px",
                  overflowY: "auto",
                  borderRadius: "8px",
                  border: "1px solid var(--card-border, #e5e7eb)",
                  backgroundColor: "var(--card-bg, #ffffff)",
                  padding: "10px 12px",
                  fontFamily:
                    'SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
                  fontSize: "12px",
                  whiteSpace: "pre-wrap",
                  wordBreak: "break-word",
                }}
              >
                {selectedErrorJob.error_message || t("errorModal.noErrorMessage")}
              </div>
            </div>
            {/* Secondary metadata below in muted style */}
            <div style={{ marginTop: "4px", fontSize: "12px", color: "#6b7280" }}>
             
              <div style={{ marginBottom: "3px" }}>
                <strong style={{ fontWeight: 500 }}>{t("errorModal.jobId")}</strong>{" "}
                <span>{selectedErrorJob.jobId}</span>
              </div>
              <div style={{ marginBottom: "3px" }}>
                <strong style={{ fontWeight: 500 }}>{t("errorModal.status")}</strong>{" "}
                <span>{statusLabel(selectedErrorJob.status)}</span>
              </div>
              {selectedErrorJob.profile_name && (
                <div style={{ marginBottom: "3px" }}>
                  <strong style={{ fontWeight: 500 }}>{t("errorModal.profileName")}</strong>{" "}
                  <span>{selectedErrorJob.profile_name}</span>
                </div>
              )}
              {selectedErrorJob.profile_type && (
                <div style={{ marginBottom: "3px" }}>
                  <strong style={{ fontWeight: 500 }}>{t("errorModal.profileType")}</strong>{" "}
                  <span>
                    {profileTypeLabels[selectedErrorJob.profile_type] ||
                      selectedErrorJob.profile_type}
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      {/* Loading spinner overlay when fetching report details */}
      {loadingReport && (
        <div style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: "rgba(0, 0, 0, 0.5)",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          zIndex: 9999
        }}>
          <div style={{
            textAlign: "center",
            color: "white"
          }}>
            <div style={{ 
              width: "60px", 
              height: "60px", 
              border: "6px solid rgba(255, 255, 255, 0.3)", 
              borderTop: "6px solid white", 
              borderRadius: "50%", 
              animation: "spin 1s linear infinite",
              margin: "0 auto 20px"
            }}></div>
            <div style={{ fontSize: "18px", fontWeight: "500" }}>
              {t("loadingReportDetails")}
            </div>
            <style>{`
              @keyframes spin {
                0% { transform: rotate(0deg); }
                100% { transform: rotate(360deg); }
              }
            `}</style>
          </div>
        </div>
      )}
      {/* Modal for detailed report */}
      <ReportModal 
        isOpen={showReportModal}
        onClose={() => {
          setShowReportModal(false);
          setSelectedReport(null);
        }}
        reportData={selectedReport}
        navigate={navigate}
      />
    </div>
  );
};

export default TestStatus;
